/**
 * Exact money text for amounts stored in minor units (Stripe `…Minor` columns), e.g.
 * (3077340, 'eur') → "€30,773.40". Works on digit strings too, so 64-bit values that a JavaScript
 * number cannot hold stay exact. Returns null for an unknown currency or a non-integer amount.
 */
export function formatMinorUnits(amount: number | string, currency: string): string | null {
  const code = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return null;
  let format: Intl.NumberFormat;
  try {
    format = new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
  } catch {
    return null;
  }
  const text =
    typeof amount === 'number' ? (Number.isInteger(amount) ? String(amount) : '') : amount.trim();
  if (!/^-?\d+$/.test(text)) return null;

  const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
  const negative = text.startsWith('-');
  const value = BigInt(negative ? text.slice(1) : text);
  const scale = 10n ** BigInt(digits);
  const whole = (value / scale).toLocaleString('en-US');
  const fraction = digits > 0 ? `.${(value % scale).toString().padStart(digits, '0')}` : '';
  const symbol =
    format.formatToParts(0).find((part) => part.type === 'currency')?.value ?? `${code} `;
  // "-0" stays "€0.00": a zero amount has no sign.
  return `${negative && value !== 0n ? '−' : ''}${symbol}${whole}${fraction}`;
}
